'use client';

import { InfiniteScrollTrigger } from '@/components/infinite-scroll-trigger';
import { QuestionCard } from '@/components/question-card';
import { QuestionListSkeleton } from '@/components/question-list-skeleton';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { cn, getRootLink } from '@/lib/utils';
import { SlugRoutes } from '@/types/api/common';
import type { QuestionInfoType } from '@/types/api/idea-starter';

interface QuestionListProps {
  questions: QuestionInfoType[];
  isLoading?: boolean;
  hideUserInfo?: boolean;
  hideReport?: boolean;
  slugRoute?: SlugRoutes;
  // New props for infinite scrolling
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
  isInfiniteScroll?: boolean;
  hideAddStoryAction?: boolean;
  isActivity?: boolean;
}

export const QuestionList = ({
  questions,
  isLoading,
  hideUserInfo = false,
  hideReport = false,
  slugRoute = 'home',
  hasNextPage = false,
  isFetchingNextPage = false,
  fetchNextPage,
  isInfiniteScroll = false,
  hideAddStoryAction = false,
  isActivity = false,
}: QuestionListProps) => {
  // Set up infinite scroll if enabled
  const { setTargetRef } = useInfiniteScroll({
    onIntersect: () => {
      if (fetchNextPage && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    enabled: isInfiniteScroll && !!fetchNextPage && hasNextPage,
  });

  return (
    <div className={cn('flex flex-col gap-4', hideAddStoryAction && 'gap-0')}>
      {isLoading && questions.length === 0 ? (
        <QuestionListSkeleton />
      ) : (
        questions.map((question) => (
          <QuestionCard
            key={question.id}
            question={question}
            hideUserInfo={hideUserInfo}
            hideReport={hideReport}
            slugRoute={getRootLink(question.questionType)}
            hideAddStoryAction={hideAddStoryAction}
            isActivity={isActivity}
          />
        ))
      )}

      {/* Render the infinite scroll trigger only if infinite scroll is enabled */}
      {isInfiniteScroll && (
        <InfiniteScrollTrigger
          isLoading={isFetchingNextPage}
          hasNextPage={hasNextPage}
          setTargetRef={setTargetRef}
        />
      )}
    </div>
  );
};
